import { computed, inject, Injectable, signal, OnDestroy } from '@angular/core';
import { LocationsWMSFilterModel, WarehouseModel, WarehouseListModel } from '@kanzi-apes/kanzi-models';
import {
  injectQuery,
  injectQueryClient,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { expand, lastValueFrom, reduce, EMPTY, Subscription } from 'rxjs';
import {
  WarehouseQueryKeys,
  WarehouseListQueryKeys,
} from './query-key.factory';
import { KanziLocationsService } from '../services/kanzi-locations.service';
import { getAuthUserWMSConfig } from '@kanzi-apes/kanzi-authenticator';
import { Store } from '@ngrx/store';
import { EnvironmentService } from '@kanzi-apes/kanzi-utils';

@Injectable({
  providedIn: 'root',
})
export class LocationsQueries implements OnDestroy {
  private readonly store = inject(Store);
  private kanziLocationsService = inject(KanziLocationsService);
  private queryClient = injectQueryClient();
  private page = signal(1);
  private locationTypeName = signal<string>('warehouse');
  private userConfigs = this.store.selectSignal(getAuthUserWMSConfig);
  private warehouseRootListFilters = signal<LocationsWMSFilterModel>({
    type__name__eq: this.locationTypeName(),
    limit: 100,
  });

  private warehouseListFilters = signal<LocationsWMSFilterModel>({
    type__name__eq: this.locationTypeName(),
  });
  public dataWarehouse = signal([]);

  private envService = inject(EnvironmentService);
  private filterWareHouse =
    this.envService.wmsEnvironment?.filter_warehouses_user;

  /** Accumulated warehouse results across all V3 pages */
  public warehouseRootResults = signal<WarehouseModel[]>([]);
  public warehouseRootLoaded = signal<boolean>(false);

  /** Subscription for warehouse root pages loading */
  private warehouseRootSubscription: Subscription | null = null;

  /**
   * Aggregated warehouse root list for downstream consumers.
   * Maps V3 response so that 'id' uses the 'pk' value (UUID),
   * ensuring backward compatibility with all consumers that use warehouse.id.
   */
  public warehouseRootListData = computed((): WarehouseListModel | null => {
    if (!this.warehouseRootLoaded()) {
      return null;
    }
    const results = this.warehouseRootResults();
    return {
      count: results.length,
      last_evaluated_key: null,
      warnings: [],
      results: results.map((warehouse) => ({
        ...warehouse,
        id: warehouse.pk,
      })),
    };
  });

  public warehouseListQuery = injectQuery(() => ({
    queryKey: WarehouseListQueryKeys.all(this.warehouseListFilters()),
    queryFn: () =>
      lastValueFrom(
        this.kanziLocationsService.getWarehouseList(this.warehouseListFilters())
      ),
    retry: 2,
    staleTime: 50000,
    placeholderData: keepPreviousData,
  }));

  /**
   * Loads all warehouse root pages from the V3 endpoint using RxJS expand.
   * Fetches sequentially until pagination.has_next is false.
   * Handles race conditions by cancelling any in-progress request before starting a new one.
   * Should be called after authentication is confirmed.
   */
  loadAllWarehouseRootPages(): void {
    // Cancel any in-progress request to prevent race conditions
    if (this.warehouseRootSubscription) {
      this.warehouseRootSubscription.unsubscribe();
      this.warehouseRootSubscription = null;
    }

    const limit = 100;
    let currentOffset = 0;

    this.warehouseRootSubscription = this.kanziLocationsService
      .getWarehouseListV3({
        type__name__eq: this.locationTypeName(),
        offset: currentOffset,
        limit,
      })
      .pipe(
        expand((response) => {
          if (response.pagination.has_next) {
            currentOffset = currentOffset + limit;
            return this.kanziLocationsService.getWarehouseListV3({
              type__name__eq: this.locationTypeName(),
              offset: currentOffset,
              limit,
            });
          }
          return EMPTY;
        }),
        reduce<any, WarehouseModel[]>(
          (acc, response) => [...acc, ...response.results],
          []
        )
      )
      .subscribe({
        next: (allResults) => {
          this.warehouseRootResults.set(allResults);
          this.warehouseRootLoaded.set(true);
        },
        error: (error) => {
          console.error('[LocationsQueries] Error loading warehouse root pages:', error);
          this.warehouseRootLoaded.set(true);
          this.warehouseRootSubscription = null;
        },
      });
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    if (this.warehouseRootSubscription) {
      this.warehouseRootSubscription.unsubscribe();
      this.warehouseRootSubscription = null;
    }
  }
}
